/* Lock-screen rest alerts (Web Push). A locked phone can't run the rest
 * timer, so the app tells the server when the rest ends and the server pushes
 * "Rest done" then (src/web_push.py; public/sw.js shows it). On iPhone this
 * needs iOS 16.4+ and the app opened from the home screen. */

const FLAG_KEY = 'xceller_fit_push'; // device-local: this device asked for alerts

export function pushSupported() {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window
    && 'Notification' in window;
}

/* iOS only offers push to a web app started from the home screen. */
export function isStandalone() {
  return window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

export function pushOn() {
  try {
    return pushSupported() && Notification.permission === 'granted' && localStorage.getItem(FLAG_KEY) === '1';
  } catch { return false; }
}

export function registerServiceWorker() {
  if (!pushSupported()) return Promise.resolve(null);
  return navigator.serviceWorker.register('/sw.js').catch(() => null);
}

const b64ToBytes = (s) => {
  const raw = atob((s + '='.repeat((4 - (s.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, c => c.charCodeAt(0));
};

const api = (token, path, method = 'GET', body) => fetch(path, {
  method,
  headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
  body: body ? JSON.stringify(body) : undefined,
  keepalive: true,
}).then(r => r.json().catch(() => ({ success: r.ok })));

/* Ask for permission (must run inside a tap), subscribe and register this
 * device with the server. Resolves to null on success or a reason. */
export async function enablePush(token) {
  if (!pushSupported()) return 'This browser has no push notifications.';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'Notifications are blocked. Allow them in Settings > Notifications.';
  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  const { public_key: key } = await api(token, '/api/push/key');
  if (!key) return 'Could not reach the server.';
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(key) });
  const res = await api(token, '/api/push/subscribe', 'POST', { subscription: sub.toJSON() });
  if (!res?.success) return res?.message || 'The server refused the subscription.';
  try { localStorage.setItem(FLAG_KEY, '1'); } catch {}
  return null;
}

export async function disablePush(token) {
  try { localStorage.removeItem(FLAG_KEY); } catch {}
  const reg = pushSupported() ? await navigator.serviceWorker.getRegistration() : null;
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (sub) {
    await api(token, '/api/push/unsubscribe', 'POST', { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
}

export function scheduleRestPush(token, endsAt, body) {
  return api(token, '/api/push/rest', 'POST', { ends_at: endsAt, body }).catch(() => null);
}

export function cancelRestPush(token) {
  return api(token, '/api/push/rest', 'DELETE').catch(() => null);
}
